import {
  COMPONENT_VALUE_TYPES as types,
  COMPONENT_COLOR_THEMES,
  PERCENTAGES_NO_ZERO_DESC,
  BUTTON_STYLE_TYPE,
} from 'shared/constants';
import { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  slides: {
    hidden: boolean;
    color_theme: string;
    slide_link?: string;
    bg_video: {
      url: string;
      mobile_url: string;
      cover: string;
    },
    bg_image: {
      enable_gradient?: boolean;
      url: string;
      mobile_url: string;
      image_alt?: string;
      responsive_urls?: string | {
        mobile: string;
        tablet: string;
        desktop: string;
      }
    },
    highlights: {
        title?: string;
        description?: string;
        title_width?: string;
        description_width?: string;
        hide_underline?: boolean;
    }[],
    button_margin_top?: string;
    buttons: {
      label?: string;
      url?: string;
      link_to_app?: boolean;
      open_in_new_tab?: boolean;
      button_type?: typeof BUTTON_STYLE_TYPE;
    }[];
  }[];
  carouselAutoplaySpeed?: string;
  controlsEnabled?: boolean;
  appDownload?: {
    url: string;
    iosStoreLink: string;
    iosAppLink: string;
    androidStoreLink: string;
    androidAppLink: string;
    androidApkLink: string;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'slides',
    label: 'Slides',
    type: types.ARRAY,
    children: [
      {
        key: 'hidden',
        label: 'Hide Slide',
        type: types.BOOLEAN,
      },
      {
        key: 'color_theme',
        label: 'Color Theme',
        type: types.ENUM,
        options: COMPONENT_COLOR_THEMES,
      },
      {
        key: 'slide_link',
        label: 'Slide Link',
        type: types.TEXT,
      },
      {
        key: 'bg_video',
        label: 'Background Video',
        type: types.OBJECT,
        children: [
          {
            key: 'url',
            label: 'Video Link',
            type: types.TEXT,
          },
          {
            key: 'mobile_url',
            label: 'Mobile Video Link',
            type: types.TEXT,
          },
          {
            key: 'cover',
            label: 'Video Cover',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'bg_image',
        label: 'Background Image',
        type: types.OBJECT,
        children: [
          {
            key: 'url',
            label: 'Url',
            type: types.TEXT,
          },
          {
            key: 'mobile_url',
            label: 'Mobile Url',
            type: types.TEXT,
          },
          {
            key: 'responsive_urls',
            label: 'Responsive Urls',
            type: types.S3_ASSET_URL,
            options: {
              cropEnabled: true,
            },
          },
          {
            key: 'image_alt',
            label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
            type: types.TEXT,
          },
          {
            key: 'enable_gradient',
            label: 'Enable top gradient',
            type: types.BOOLEAN,
          },
        ],
      },
      {
        key: 'highlights',
        label: 'Highlights',
        type: types.ARRAY,
        children: [
          {
            key: 'title',
            label: 'Title',
            type: types.TEXT
          },
          {
            key: 'description',
            label: 'Description',
            type: types.TEXT,
            options: {
              maxLength: 300,
            },
          },
          {
            key: 'subtitle',
            label: 'Subtitle',
            type: types.TEXT,
            options: {
              maxLength: 100,
            },
          },
          {
            key: 'title_width',
            label: 'Desktop: Title Width (Percent)',
            type: types.ENUM,
            options: PERCENTAGES_NO_ZERO_DESC,
          },
          {
            key: 'description_width',
            label: 'Desktop: Description Width (Percent)',
            type: types.ENUM,
            options: PERCENTAGES_NO_ZERO_DESC,
          },
          {
            key: 'hide_underline',
            label: 'Hide Underline',
            type: types.BOOLEAN,
          },
        ],
      },
      {
        key: 'button_margin_top',
        label: 'Button Margin Top',
        type: types.TEXT,
      },
      {
        key: 'buttons',
        label: 'Buttons',
        type: types.ARRAY,
        children: [
          {
            key: 'label',
            label: 'Button Label',
            type: types.TEXT,
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
          {
            key: 'link_to_app',
            label: 'Link to App (ignores URL)',
            type: types.BOOLEAN,
            tooltip: `Configures the button to try to open the app, the app store, or the mobile-app page, depending on the user's device and app install status. This overrides the URL prop.`,
          },
          {
            key: 'open_in_new_tab',
            label: 'Open in New Tab',
            type: types.BOOLEAN
          },
          {
            key: 'button_type',
            label: 'Button Type',
            type: types.ENUM,
            options: BUTTON_STYLE_TYPE
          },
        ],
      },
    ],
  },
  {
    key: 'carouselAutoplaySpeed',
    label: 'Autoplay Speed (in seconds)',
    type: types.TEXT,
  },
  {
    key: 'controlsEnabled',
    label: 'Navigation Controls',
    type: types.BOOLEAN,
  },
  {
    key: 'appDownload',
    label: 'App Store Settings',
    type: types.OBJECT,
    children: [
      {
        key: 'url',
        label: 'URL',
        type: types.TEXT,
      },
      {
        key: 'iosStoreLink',
        label: 'iOS Store link',
        type: types.TEXT,
      },
      {
        key: 'iosAppLink',
        label: 'iOS App link',
        type: types.TEXT,
      },
      {
        key: 'androidStoreLink',
        label: 'Android Store Link',
        type: types.TEXT,
      },
      {
        key: 'androidAppLink',
        label: 'Android App Link',
        type: types.TEXT,
      },
      {
        key: 'androidApkLink',
        label: 'Android Apk Link',
        type: types.TEXT,
      },
    ]
  },
];

const defaultProps: Props = {
  slides: [
    {
      hidden: false,
      color_theme: 'DARK',
      bg_image: {
        url: '',
        image_alt: '',
        mobile_url: '',
        enable_gradient: false,
      },
      bg_video: {
        url: 'https://genesis-cdn.ff.com/homepage/01_marquee/genesis_ff91_emergence_3mbps.mp4',
        mobile_url: '',
        cover: 'https://genesis-cdn.ff.com/homepage/01_marquee/genesis_cover_image.jpg'
      },
      highlights: [],
      buttons: []
    }
  ],
  carouselAutoplaySpeed: '5',
  controlsEnabled: true,
  appDownload: {
    url: 'https://www.ff.com/us/mobile-app',
    iosStoreLink: 'https://itunes.apple.com/us/app/id1600637235?ls=1&mt=8',
    iosAppLink: 'https://faradayfuture2023.app.link/YajHk0j7Nxb',
    androidStoreLink: 'https://play.google.com/store/apps/details?id=com.faradayfuture.online.foreign',
    androidAppLink: 'faradayfuture://feed',
    androidApkLink: '',
  },
};

export default {
  title: 'HomepageCarousel',
  id: 'HomepageCarousel',
  widgetImage: '/widgetImages/Page+carousel.png',
  basic: false,
  propsSchema,
  defaultProps
};
